// @boty/ai - Bot Name Suggestion
// Generates a suggested name for a bot based on its content

import { getOpenAIClient, AI_MODELS, handleOpenAIError } from './provider';
import type { AIUsageCallback } from './types';

export interface NameSuggestionResult {
  name: string;
  description: string;
  generatedAt: string;
}

export interface NameSuggestionOptions {
  /** Optional callback for reporting LLM usage data */
  onUsage?: AIUsageCallback;
}

/**
 * Generates a suggested name for a bot based on its content
 *
 * @param content - The bot's combined content
 * @param options - Optional settings including usage callback
 * @returns A suggested name for the bot
 *
 * @example
 * ```typescript
 * const result = await generateBotName("Event details: Ping Pong Tournament on March 15th...");
 * console.log(result.name); // "Ping Pong Tournament FAQ"
 * ```
 */
export async function generateBotName(
  content: string,
  options: NameSuggestionOptions = {}
): Promise<NameSuggestionResult> {
  const { onUsage } = options;
  const client = getOpenAIClient();

  // Truncate content if too long (keep first 4000 chars for context)
  const truncatedContent =
    content.length > 4000 ? content.slice(0, 4000) + '...' : content;

  try {
    const startTime = Date.now();
    const response = await client.chat.completions.create({
      model: AI_MODELS.GPT4O_MINI,
      messages: [
        {
          role: 'system',
          content: `You are a helpful assistant that suggests a short name and a brief description for chatbots based on their content.

Name rules:
- 2-5 words
- Describes what the bot helps with
- Simple and memorable
- Don't use words like "Bot", "Assistant", "AI", or "Helper"
- Examples: "Wedding FAQ", "Tech Conference Info", "Gym Schedule"

Description rules:
- 1-2 sentences (max 150 characters)
- Summarizes what the bot can answer questions about
- Written for end users (e.g. "Ask me about parking, schedule, and venue details for the tech conference.")

Return a JSON object with "name" and "description" fields. Example:
{"name": "Tech Conference Info", "description": "Ask me about speakers, schedule, and venue details for TechCon 2026."}`,
        },
        {
          role: 'user',
          content: `Based on this content, suggest a name and description for a chatbot:\n\n${truncatedContent}`,
        },
      ],
      temperature: 0.7,
      max_tokens: 200,
      response_format: { type: 'json_object' },
    });

    if (onUsage) {
      try {
        onUsage({
          model: AI_MODELS.GPT4O_MINI,
          provider: 'openai',
          inputTokens: response.usage?.prompt_tokens ?? 0,
          outputTokens: response.usage?.completion_tokens ?? 0,
          latencyMs: Date.now() - startTime,
          functionName: 'generateBotName',
          inputMessages: [
            { role: 'system', content: 'Suggest a name and description for a chatbot.' },
            { role: 'user', content: truncatedContent.slice(0, 500) },
          ],
          ...(response.choices[0]?.message?.content && { outputText: response.choices[0].message.content.slice(0, 500) }),
        });
      } catch { /* never break the call */ }
    }

    const raw = response.choices[0]?.message?.content?.trim() || '{}';
    let parsed: { name?: string; description?: string };
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = { name: raw, description: '' };
    }

    return {
      name: parsed.name || 'My Bot',
      description: parsed.description || '',
      generatedAt: new Date().toISOString(),
    };
  } catch (error) {
    handleOpenAIError(error, 'generateBotName');
  }
}
